// components/HealthStatusPage.js
import React from "react";
import { useHealth } from "../contexts/HealthContext";

const HealthStatusPage = () => {
    const { summary, loadingSummary } = useHealth();
    const fixedTaxonomyEnabled = window.appSettings?.fixedTaxonomyEnabled;

    // Determine overall health status
    let isOk = summary && summary.recs_ready_1h > 0;
    if (isOk && fixedTaxonomyEnabled && summary?.recs_fixedintent_1h < 1) {
        isOk = false;
    }

    // Determine if we have warning status (yellow)
    let isWarning = false;
    if (summary && !isOk && summary.recs_ready_24h > 0) {
        if (fixedTaxonomyEnabled) {
            isWarning = summary.recs_fixedintent_24h > 0;
        } else {
            isWarning = true;
        }
    }

    // Format the date
    const formattedDate = summary?.health_date
        ? new Date(summary.health_date).toLocaleString()
        : "N/A";

    // Calculate time since last update
    const getTimeSinceUpdate = () => {
        if (!summary?.health_date) return "";

        const now = new Date();
        const updateTime = new Date(summary.health_date);
        const diffMs = now - updateTime;

        // Convert to appropriate time unit
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffMonths > 0) return `(${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago)`;
        if (diffDays > 0) return `(${diffDays} day${diffDays !== 1 ? 's' : ''} ago)`;
        if (diffHours > 0) return `(${diffHours} hour${diffHours !== 1 ? 's' : ''} ago)`;
        return `(${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago)`;
    };

    // Calculate error rates
    const syncErrorRate = summary?.sync_called_1h > 0
        ? ((summary.sync_errors_1h / summary.sync_called_1h) * 100).toFixed(1)
        : "0";

    const combineErrorRate = summary?.combine_called_1h > 0
        ? ((summary.combine_errors_1h / summary.combine_called_1h) * 100).toFixed(1)
        : "0";

    const apiErrorRate1h = summary?.api_called_1h > 0
        ? ((summary.api_errors_1h / summary.api_called_1h) * 100).toFixed(1)
        : "0";

    const apiErrorRate24h = summary?.api_called_24h > 0
        ? ((summary.api_errors_24h / summary.api_called_24h) * 100).toFixed(1)
        : "0";

    // Health status color and text
    const statusColor = isOk ? "bg-green-600" : (isWarning ? "bg-yellow-500" : "bg-red-600");
    const statusText = isOk ? "Healthy" : (isWarning ? "Warning" : "Needs Attention");

    // Background color helper function
    const getBgColor = (value, isError = false, threshold = 0) => {
        if (isError && value > threshold) return "bg-red-50";
        if (!isError && value > threshold) return "bg-green-50";
        return "";
    };

    // Get any issues to display
    const getIssues = () => {
        const issues = [];

        if (summary?.recs_ready_1h === 0)
            issues.push("No records processed in the last hour");

        if (fixedTaxonomyEnabled && summary?.recs_fixedintent_1h === 0)
            issues.push("No fixed intent calls in the last hour");

        if (parseFloat(syncErrorRate) > 5)
            issues.push(`High sync error rate: ${syncErrorRate}%`);

        if (parseFloat(combineErrorRate) > 5)
            issues.push(`High combine error rate: ${combineErrorRate}%`);

        if (parseFloat(apiErrorRate1h) > 5)
            issues.push(`High API error rate (1h): ${apiErrorRate1h}%`);

        return issues;
    };

    const issues = getIssues();
    const timeSinceUpdate = getTimeSinceUpdate();

    // Subheader style
    const subheaderStyle = "font-semibold text-gray-800 text-md mb-3";

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Health Status</h2>

            {loadingSummary ? (
                <div className="flex items-center justify-center h-64">
                    <svg className="animate-spin h-10 w-10  usan-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Health Overview */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">Current Status</h3>
                            <div className="flex items-center">
                                <div className={`w-4 h-4 ${statusColor} rounded-full mr-2`}></div>
                                <span className="font-medium">{statusText}</span>
                            </div>
                        </div>

                        <p className="text-gray-600 mb-2">
                            Last updated: <span className="font-medium">{formattedDate}</span> <span className="text-gray-500">{timeSinceUpdate}</span>
                        </p>

                        {issues.length > 0 && (
                            <div className="mt-3 mb-4">
                                <p className="font-medium text-red-600 mb-2">Issues detected:</p>
                                <ul className="list-disc pl-5 text-red-600">
                                    {issues.map((issue, index) => (
                                        <li key={index}>{issue}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="hidden mt-4">
                            <button
                                className="bg-gray-300 text-gray-500 px-4 py-2 rounded font-medium cursor-not-allowed opacity-60"
                                disabled
                            >
                                Run Health Benchmark (Coming Soon)
                            </button>
                        </div>
                    </div>

                    {/* Processed Contacts */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b border-gray-200 px-6 py-4">
                                <h3 className="text-lg font-medium">Processed Contacts</h3>
                        </div>
                        {fixedTaxonomyEnabled ? (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">New (1 hour):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_new_1h, false)}`}>{summary?.recs_new_1h}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">New (24 hours):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_new_24h, false)}`}>{summary?.recs_new_24h}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ready (1 hour):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_ready_1h, false)}`}>{summary?.recs_ready_1h}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ready (24 hours):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_ready_24h, false)}`}>{summary?.recs_ready_24h}</span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                        <h4 className={subheaderStyle}>Fixed Intent Contacts</h4>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ready (1 hour):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_fixedintent_1h, false)}`}>{summary?.recs_fixedintent_1h}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ready (24 hours):</span>
                                        <span className={`font-medium ${getBgColor(summary?.recs_fixedintent_24h, false)}`}>{summary?.recs_fixedintent_24h}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                    <div className="text-gray-600 text-sm mb-1">New (1 hour)</div>
                                    <div className="text-xl font-bold">{summary?.recs_new_1h || 0}</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                    <div className="text-gray-600 text-sm mb-1">New (24 hours)</div>
                                    <div className="text-xl font-bold">{summary?.recs_new_24h || 0}</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                    <div className="text-gray-600 text-sm mb-1">Ready (1 hour)</div>
                                    <div className="text-xl font-bold">{summary?.recs_ready_1h || 0}</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                    <div className="text-gray-600 text-sm mb-1">Ready (24 hours)</div>
                                    <div className="text-xl font-bold">{summary?.recs_ready_24h || 0}</div>
                                </div>
                            </div>
                        )}

                        {/* Models Used Subsection */}
                        <div className="border-t border-gray-200 px-6 py-4">
                            <h4 className={subheaderStyle}>Models Used</h4>
                            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                                <div className="flex flex-col">
                                    <span className="text-gray-600 text-sm">Amazon Bedrock - Claude Haiku 3</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-gray-600 text-sm">Amazon Bedrock - Claude Sonnet 3.5</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detection */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h3 className="text-lg font-medium">New Intents</h3>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Major Intents</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">New (1 hour):</span>
                                    <span className={`font-medium ${getBgColor(summary?.new_majorintents_1h, false)}`}>{summary?.new_majorintents_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">New (24 hours):</span>
                                    <span className={`font-medium ${getBgColor(summary?.new_majorintents_24h, false)}`}>{summary?.new_majorintents_24h}</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Categories</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">New (1 hour):</span>
                                    <span className={`font-medium ${getBgColor(summary?.new_categories_1h, false)}`}>{summary?.new_categories_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">New (24 hours):</span>
                                    <span className={`font-medium ${getBgColor(summary?.new_categories_24h, false)}`}>{summary?.new_categories_24h}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* System Operations */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h3 className="text-lg font-medium">System Operations</h3>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Sync Operations</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total calls:</span>
                                    <span className="font-medium">{summary?.sync_called_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Errors:</span>
                                    <span className={`font-medium ${getBgColor(summary?.sync_errors_1h, true)}`}>{summary?.sync_errors_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Error rate:</span>
                                    <span className={`font-medium ${getBgColor(parseFloat(syncErrorRate), true)}`}>{syncErrorRate}%</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Combine Operations</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total calls:</span>
                                    <span className="font-medium">{summary?.combine_called_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Errors:</span>
                                    <span className={`font-medium ${getBgColor(summary?.combine_errors_1h, true)}`}>{summary?.combine_errors_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Error rate:</span>
                                    <span className={`font-medium ${getBgColor(parseFloat(combineErrorRate), true)}`}>{combineErrorRate}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* API Usage */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h3 className="text-lg font-medium">Website Usage</h3>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Last Hour</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total calls:</span>
                                    <span className="font-medium">{summary?.api_called_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Errors:</span>
                                    <span className={`font-medium ${getBgColor(summary?.api_errors_1h, true)}`}>{summary?.api_errors_1h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Error rate:</span>
                                    <span className={`font-medium ${getBgColor(parseFloat(apiErrorRate1h), true)}`}>{apiErrorRate1h}%</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className={subheaderStyle}>Last 24 Hours</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total calls:</span>
                                    <span className="font-medium">{summary?.api_called_24h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Errors:</span>
                                    <span className={`font-medium ${getBgColor(summary?.api_errors_24h, true)}`}>{summary?.api_errors_24h}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Error rate:</span>
                                    <span className={`font-medium ${getBgColor(parseFloat(apiErrorRate24h), true)}`}>{apiErrorRate24h}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* History Section (Coming Soon) */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-400">Health Status History (Coming Soon)</h3>
                        <div className="mt-4 h-48 bg-gray-200 rounded-md flex items-center justify-center">
                            <p className="text-gray-500">Historical health data will be displayed here</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthStatusPage;